import React, { useState } from 'react';
import {
  Tag,
  Space,
  Modal,
  Select,
  message,
  Typography,
  Row,
  Col,
  Card,
  Statistic,
  Descriptions,
} from 'antd';
import {
  EyeOutlined,
  StopOutlined,
  ShoppingOutlined,
  CalendarOutlined,
} from '@ant-design/icons';

import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { useSubscriptions } from '../../hooks';
import { DataTable } from '../../components';

const { Text } = Typography;
const { Option } = Select;

const planColors: Record<string, string> = {
  lite: 'default',
  starter: 'blue',
  professional: 'purple',
  enterprise: 'gold',
};

const statusColors: Record<string, string> = {
  active: 'success',
  pending: 'warning',
  past_due: 'error',
  canceled: 'default',
};

export const SubscriptionList: React.FC = () => {
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<any>(null);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const {
    subscriptions,
    loading,
    meta,
    filters,
    setFilters,
    refetch,
    cancelSubscription,
  } = useSubscriptions();

  const handleFilterChange = (key: string, value: any) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters({ ...filters, page, limit: pageSize });
  };

  const handleCancel = async (id: string) => {
    try {
      await cancelSubscription(id, cancelReason);
      message.success('Subscription cancelled successfully');
      setIsCancelModalVisible(false);
      setCancelReason('');
    } catch {
      message.error('Failed to cancel subscription');
    }
  };



  const columns: ColumnsType<any> = [
    {
      title: 'Tenant',
      dataIndex: 'tenantName',
      key: 'tenantName',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.tenantId}</Text>
        </Space>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      width: 120,
      render: (plan) => (
        <Tag color={planColors[plan] || 'default'}>{plan.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={statusColors[status] || 'default'}>
          {status.replace('_', ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Billing',
      dataIndex: 'billingCycle',
      key: 'billingCycle',
      width: 100,
      render: (cycle, record) => (
        <Space direction="vertical" size={0}>
          <Tag>{cycle === 'monthly' ? 'Monthly' : 'Yearly'}</Tag>
          <Text style={{ fontSize: 12 }}>
            ₫{record.price?.toLocaleString()}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Next Billing',
      dataIndex: 'nextBillingDate',
      key: 'nextBillingDate',
      width: 150,
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Auto Renew',
      dataIndex: 'autoRenew',
      key: 'autoRenew',
      width: 100,
      render: (autoRenew) => (
        <Tag color={autoRenew ? 'success' : 'default'}>
          {autoRenew ? 'ON' : 'OFF'}
        </Tag>
      ),
    },
  ];

  const filterContent = (
    <Row gutter={16}>
      <Col span={8}>
        <Select
          placeholder="Filter by status"
          allowClear
          style={{ width: '100%' }}
          value={filters.status}
          onChange={(value) => handleFilterChange('status', value)}
        >
          <Option value="active">Active</Option>
          <Option value="pending">Pending</Option>
          <Option value="past_due">Past Due</Option>
          <Option value="canceled">Canceled</Option>
        </Select>
      </Col>
      <Col span={8}>
        <Select
          placeholder="Filter by plan"
          allowClear
          style={{ width: '100%' }}
          value={filters.plan}
          onChange={(value) => handleFilterChange('plan', value)}
        >
          <Option value="lite">Lite</Option>
          <Option value="starter">Starter</Option>
          <Option value="professional">Professional</Option>
          <Option value="enterprise">Enterprise</Option>
        </Select>
      </Col>
    </Row>
  );

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Subscriptions"
              value={subscriptions.filter((s) => s.status === 'active').length}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Monthly Revenue"
              value={subscriptions
                .filter((s) => s.status === 'active' && s.billingCycle === 'monthly')
                .reduce((sum, s) => sum + s.price, 0)}
              prefix="₫"
              valueStyle={{ color: '#1890ff' }}
              formatter={(value) => formatCurrency(value as number)}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Yearly Subscriptions"
              value={subscriptions.filter((s) => s.billingCycle === 'yearly').length}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Past Due"
              value={subscriptions.filter((s) => s.status === 'past_due').length}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      <DataTable
        title="Subscriptions"
        data={subscriptions}
        columns={columns}
        loading={loading}
        rowKey="id"
        onRefresh={refetch}
        filters={filterContent}
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          onChange: handlePageChange,
        }}
        actionColumn={{
          width: 100,
          fixed: 'right',
          actions: (record) => [
            {
              key: 'view',
              label: 'View Details',
              icon: <EyeOutlined />,
              onClick: () => {
                setSelectedSubscription(record);
                setIsDetailModalVisible(true);
              },
            },
            ...(record.status === 'active'
              ? [
                  {
                    key: 'cancel',
                    label: 'Cancel',
                    icon: <StopOutlined />,
                    danger: true,
                    onClick: () => {
                      setSelectedSubscription(record);
                      setIsCancelModalVisible(true);
                    },
                  },
                ]
              : []),
          ],
        }}
      />

      {/* Detail Modal */}
      <Modal
        title="Subscription Details"
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedSubscription && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Subscription ID" span={2}>
              {selectedSubscription.id}
            </Descriptions.Item>
            <Descriptions.Item label="Tenant">
              {selectedSubscription.tenantName}
            </Descriptions.Item>
            <Descriptions.Item label="Plan">
              <Tag color={planColors[selectedSubscription.plan]}>
                {selectedSubscription.plan.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColors[selectedSubscription.status]}>
                {selectedSubscription.status.replace('_', ' ').toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Billing Cycle">
              {selectedSubscription.billingCycle === 'monthly' ? 'Monthly' : 'Yearly'}
            </Descriptions.Item>
            <Descriptions.Item label="Price">
              {formatCurrency(selectedSubscription.price)}
            </Descriptions.Item>
            <Descriptions.Item label="Start Date">
              {dayjs(selectedSubscription.startDate).format('YYYY-MM-DD')}
            </Descriptions.Item>
            <Descriptions.Item label="End Date">
              {dayjs(selectedSubscription.endDate).format('YYYY-MM-DD')}
            </Descriptions.Item>
            <Descriptions.Item label="Next Billing">
              {dayjs(selectedSubscription.nextBillingDate).format('YYYY-MM-DD')}
            </Descriptions.Item>
            <Descriptions.Item label="Auto Renew">
              {selectedSubscription.autoRenew ? 'Yes' : 'No'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Cancel Modal */}
      <Modal
        title="Cancel Subscription"
        open={isCancelModalVisible}
        onCancel={() => {
          setIsCancelModalVisible(false);
          setCancelReason('');
        }}
        onOk={() => selectedSubscription && handleCancel(selectedSubscription.id)}
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to cancel the subscription for{' '}
          <strong>{selectedSubscription?.tenantName}</strong>?
        </p>
        <p>This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default SubscriptionList;
